import flight from 'flightjs'; // eslint-disable-line no-restricted-imports
import withChildComponents from 'flight-with-child-components';
import TagDisplay from 'component_ui/tag-display';
import TagInput from 'component_ui/tag-input';
import TagSuggestions from 'component_ui/tag-suggestions';
import TagRemoteSuggestionsData from 'component_data/remote-tag-suggestions';
import TagLocalSuggestionsData from 'component_data/local-tag-suggestions';
import dot from 'dot/doT';
import $ from 'jquery'; // eslint-disable-line no-restricted-imports

function TagEditor() {
    this.defaultAttrs({
        tagDivider: '\n',
        dataSource: '',
        dataSourceLocation: 'remote',
        submitOnTagChange: false,
        allowCreateTag: true,
        disallowDefault: false,
        caseSensitive: true,

        // Selectors
        inputSelector: '[data-tag-input]',
        displaySelector: '[data-tag-display]',
        suggestionsListSelector: '[data-tag-suggestions]',
        tagInputs: '[data-tag-editor-tag-inputs]',
        tagInputName: '[data-tag-editor-tag-input-name]',
        tagInput: '[data-tag-editor-tag-input]',
    });

    // Add tag-string from form element (textarea)
    this.addTagNameToForm = function(event, data) {
        const tagName = data.tagName;
        this.addTag(tagName);
        this.checkForMaxTags();
        this.select('inputSelector').focus();
        if (this.attr.submitOnTagChange) {
            this.$node.parents('form').submit();
        }
    };

    this.checkForMaxTags = function() {
        const tags = this.getTags();
        if (tags.length >= this.$node.attr('data-max-tags')) {
            this.select('inputSelector')
                .prop('disabled', true)
                .attr('placeholder', this.$node.attr('data-tag-editor-max-tags-msg'));
        } else {
            this.select('inputSelector')
                .prop('disabled', false)
                .attr('placeholder', this.$node.attr('data-tag-editor-add-tag'));
        }
    };

    // Read, split and broadcast existing tags
    this.provideExistingTags = function() {
        this.select('displaySelector').triggerHandler('uiExistingTagsAvailable', {
            existingTags: this.getTags(),
        });
    };

    // Add existing tags, then delegate to suggestions list
    this.addExistingTags = function(event, data) {
        const dataWithExistingTags = data;
        dataWithExistingTags.existingTags = this.getTags();
        this.triggerSuggestionsListEvent(event, data);
    };

    // Remove tag-string from form element (textarea)
    this.removeTagNameFromForm = function(event, data) {
        const tagName = data.tagName.trim();
        this.removeTag(tagName);
        this.checkForMaxTags();
        if (this.attr.submitOnTagChange) {
            this.$node.parents('form').submit();
        }
    };

    // Trigger event on suggestion list, avoid bubbling
    this.triggerSuggestionsListEvent = function(event, data) {
        this.select('suggestionsListSelector').triggerHandler(event.type, data);
    };

    // Trigger event on suggestion list, avoid bubbling
    this.triggerInputEvent = function(event, data) {
        this.select('inputSelector').triggerHandler(event.type, data);
    };

    // Trigger event on suggestion list, avoid bubbling
    this.triggerDisplayEvent = function(event, data) {
        this.select('displaySelector').triggerHandler(event.type, data);
    };

    // Get a list of tags
    this.getTags = function() {
        const tags = [];
        this.select('tagInput').each(function() {
            tags.push(this.value);
        });
        return tags;
    };

    // Add tag to inputs
    this.addTag = function(tagName) {
        const tags = this.getTags();
        if ($.inArray(tagName, tags)) {
            const inputName = this.select('tagInputName').attr('data-tag-editor-tag-input-name');
            const templateInput =
                '<input type="hidden" name="{{!it.inputName}}" value="{{!it.tagName}}" data-tag-editor-tag-input="">';
            const template = dot.template(templateInput);
            const templateHTML = template({
                inputName,
                tagName,
            });
            this.select('tagInputs').append(templateHTML);
        }
    };

    // Remove tag from input
    this.removeTag = function(tagName) {
        this.select('tagInput')
            .filter(`[value="${tagName}"]`)
            .remove();
    };

    this.replaceTags = function(event, data) {
        const self = this;
        const tags = data.tags || [];
        this.select('tagInputs').html('');
        tags.forEach(tag => {
            self.addTag(tag.value);
        });
        this.select('displaySelector').triggerHandler('uiReplaceTags', data);
    };

    // Check to see if text input value is in tags, if not, select it
    this.attemptSelectFromInput = function() {
        const tagName = this.select('inputSelector')
            .val()
            .trim();
        const tags = this.getTags();
        if ($.inArray(tagName, tags) === -1) {
            if (!this.attr.disallowDefault) {
                this.trigger('uiSuggestionsSelected', {
                    tagName,
                });
                this.trigger('uiSuggestionsAbort', {
                    userString: '',
                });
            }
        }
    };

    this.after('initialize', function() {
        // Attach data components
        if (this.attr.dataSourceLocation === 'local') {
            TagLocalSuggestionsData.attachTo(this.$node, {
                caseSensitive: this.attr.caseSensitive,
            });
        } else {
            TagRemoteSuggestionsData.attachTo(this.$node, {
                dataSource: this.attr.dataSource,
            });
        }

        // Attach ui components
        TagDisplay.attachTo(this.select('displaySelector'));
        TagInput.attachTo(this.select('inputSelector'), {
            caseSensitive: this.attr.caseSensitive,
        });
        TagSuggestions.attachTo(this.select('suggestionsListSelector'), {
            allowCreateTag: this.attr.allowCreateTag,
            disallowDefault: this.attr.disallowDefault,
        });

        // Remove tagname from from textarea, when deleted in display
        this.on('uiTagDeleted', this.removeTagNameFromForm);

        // Delegate keyboard events to suggestions list
        this.on('uiSuggestionsMoveUp', this.triggerSuggestionsListEvent);
        this.on('uiSuggestionsMoveDown', this.triggerSuggestionsListEvent);
        this.on('uiSuggestionsConfirm', this.triggerSuggestionsListEvent);
        this.on('uiSuggestionsAbort', this.triggerSuggestionsListEvent);

        // Select tag from text input if not already in tags
        this.on('uiAttemptSelectFromInput', this.attemptSelectFromInput);

        // Replace all inputs and tags with new data
        this.on('uiReplaceTags', this.replaceTags);

        // Add existing tags to suggestions data
        this.on('dataSuggestionsDataServed', this.addExistingTags);

        // Delegate suggestion selected event to sub components
        this.on('uiSuggestionsSelected', this.triggerInputEvent);
        this.on('uiSuggestionsSelected', this.triggerDisplayEvent);
        this.on('uiSuggestionsSelected', this.addTagNameToForm);

        // Provide existing tags from markup
        this.provideExistingTags();

        // Conditionally disable input if tag limit reached
        this.checkForMaxTags();
    });
}

export default flight.component(TagEditor, withChildComponents);



// WEBPACK FOOTER //
// ./src/js/component_ui/tag-editor.js